import React, { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { ForumProvider, useForum } from "./ForumContext.jsx";
import backgroundImg from "../Images/backgroundImg.jpg";
import "./Forum.css";

const API = process.env.REACT_APP_API_URL || "https://spar-hub.org";

function ForumSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const search = useCallback((val) => {
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/api/forum/search?q=${encodeURIComponent(val.trim())}`);
        const data = await res.json();
        setResults(data.threads || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 400);
  }, []);

  const onChange = (e) => { setQ(e.target.value); search(e.target.value); };
  const clear = () => { setQ(""); setResults(null); };

  return (
    <div className="forum-search-wrap">
      <div className="forum-search-bar">
        <svg className="forum-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          className="forum-search-input"
          placeholder="ابحث في المنتدى… Search threads…"
          value={q}
          onChange={onChange}
          dir="auto"
        />
        {q && <button className="forum-search-clear" onClick={clear}>✕</button>}
      </div>

      {results !== null && (
        <div className="forum-search-results">
          {searching && <div className="forum-search-msg">جاري البحث…</div>}
          {!searching && results.length === 0 && <div className="forum-search-msg">لا نتائج · No results</div>}
          {!searching && results.map(t => (
            <Link key={t._id} to={`/Forum/thread/${t._id}`} className="forum-search-row" onClick={clear}>
              <div className="forum-search-title">{t.title_ar || t.title_en}</div>
              <div className="forum-search-meta">
                {t.categoryId?.name_ar} · {t.authorName} · {t.replyCount} رد
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const DISCIPLINE_ICONS = {
  "painting-photography": "🎨",
  "sculpture-ceramics":   "🏺",
  "calligraphy":          "✍️",
  "digital-art":          "💻",
  "printmaking":          "🖨️",
  "photography":          "📷",
  "office-organisation":  "🗂️",
  "office-academic":      "🎓",
  "office-union":         "⚖️",
  "office-political":     "🏛️",
  "archive-2024":         "📁",
  "archive-2023":         "📁",
};

function ForumHome() {
  const { isLoggedIn } = useAuth();
  const { categories, stats, isMember } = useForum();
  const navigate = useNavigate();

  const sections = categories.filter(c => c.type === "section");
  const offices  = categories.filter(c => c.type === "office");
  const archives = categories.filter(c => c.type === "archive");

  return (
    <div className="forum-page">

      {/* Hero — full viewport width */}
      <div className="forum-hero" style={{ backgroundImage: `url(${backgroundImg})` }}>
        <div className="forum-hero-overlay" />
        <div className="forum-hero-content">
          <div className="forum-hero-ar">منتدى SPAR</div>
          <div className="forum-hero-sub">SPAR Artists Forum · المنتدى الفني</div>
          <div className="forum-hero-tagline">ناقش · شارك · اكتشف &nbsp;|&nbsp; بي فنك وصوتك تحدث تغير</div>
        </div>
      </div>

      {/* Constrained content */}
      <div className="forum-wrap">

        {/* Search */}
        <ForumSearch />

        {/* Stats */}
        <div className="forum-stats">
          <div className="forum-stat-card">
            <div className="forum-stat-num">{stats.threads.toLocaleString("ar-EG")}</div>
            <div className="forum-stat-lbl">نقاش · Threads</div>
          </div>
          <div className="forum-stat-card">
            <div className="forum-stat-num">{stats.posts.toLocaleString("ar-EG")}</div>
            <div className="forum-stat-lbl">مشاركة · Posts</div>
          </div>
          <div className="forum-stat-card">
            <div className="forum-stat-num">{stats.members.toLocaleString("ar-EG")}</div>
            <div className="forum-stat-lbl">عضو · Members</div>
          </div>
        </div>

        {/* Collections row */}
        <div className="forum-collections">
          {[
            { icon: "📚", ar: "رف الكتب",        en: "Bookshelf",  to: "/Forum/bookshelf"  },
            { icon: "🎵", ar: "المكتبة الصوتية",  en: "Audio",      to: "/Forum/audio"      },
            { icon: "📋", ar: "الوثائق المختارة", en: "Documents",  to: "/Forum/documents"  },
            { icon: "🗃️", ar: "الأرشيف الفني",   en: "Art Archive",to: "/Forum/archive"    },
          ].map(c => (
            <div key={c.to} className="forum-collection-card" onClick={() => navigate(c.to)}>
              <div className="forum-collection-icon">{c.icon}</div>
              <div className="forum-collection-ar">{c.ar}</div>
              <div className="forum-collection-en">{c.en}</div>
            </div>
          ))}
        </div>

        {/* Membership gate */}
        {!isMember && (
          <div className="forum-gate">
            <svg width="24" height="24" fill="none" stroke="var(--green)" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <div className="forum-gate-text">
              <strong>المنتدى حصري لأعضاء SPAR وSPAU</strong> — للمشاركة في النقاشات يجب أن تكون عضواً في أحد التجمّعين ومسجّلاً في المنتدى.
            </div>
            <div className="forum-gate-links">
              {isLoggedIn ? (
                <Link to="/Forum/register" className="forum-gate-link">انضم للمنتدى ←</Link>
              ) : (
                <>
                  <Link to="/login" className="forum-gate-link">دخول SPAR ←</Link>
                  <a href="https://spau-artists.org" target="_blank" rel="noopener noreferrer" className="forum-gate-link outline">SPAU ←</a>
                </>
              )}
            </div>
          </div>
        )}

        {/* Art Disciplines */}
        {sections.length > 0 && (
          <>
            <div className="forum-section-title"><span>🎨</span> أقسام الفنون</div>
            <div className="forum-cats">
              {sections.map(cat => (
                <Link key={cat._id} to={`/Forum/category/${cat.slug}`} className="forum-cat-card">
                  <div className="forum-cat-icon">{cat.icon || DISCIPLINE_ICONS[cat.slug] || "💬"}</div>
                  <div className="forum-cat-name">{cat.name_ar}</div>
                  <div className="forum-cat-en">{cat.name_en}</div>
                  <div className="forum-cat-meta">
                    <span>🧵 {cat.threadCount}</span>
                    <span>💬 {cat.postCount}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* SPAR Offices */}
        {offices.length > 0 && (
          <>
            <div className="forum-section-title"><span>🏛️</span> مكاتب SPAR</div>
            <div className="forum-cats">
              {offices.map(cat => (
                <Link key={cat._id} to={`/Forum/category/${cat.slug}`} className="forum-cat-card office">
                  <div className="forum-cat-icon">{cat.icon || DISCIPLINE_ICONS[cat.slug] || "🏛️"}</div>
                  <div className="forum-cat-name">{cat.name_ar}</div>
                  <div className="forum-cat-en">{cat.name_en}</div>
                  <div className="forum-cat-meta">
                    <span>🧵 {cat.threadCount}</span>
                    <span>💬 {cat.postCount}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Archive */}
        {archives.length > 0 && (
          <>
            <div className="forum-section-title"><span>📁</span> الأرشيف</div>
            <div className="forum-archive-rows">
              {archives.map(cat => (
                <Link key={cat._id} to={`/Forum/category/${cat.slug}`} className="forum-archive-row">
                  <span className="forum-archive-icon">{cat.icon || "📁"}</span>
                  <span className="forum-archive-name">{cat.name_ar} — {cat.name_en}</span>
                  <span className="forum-archive-count">{cat.threadCount} نقاش</span>
                </Link>
              ))}
              <div className="forum-archive-row" style={{ opacity: 0.7 }}>
                <span className="forum-archive-icon">💬</span>
                <span className="forum-archive-name">مقالات WhatsApp المنقولة</span>
                <span className="forum-archive-count">٣٬٨١٢ مشاركة</span>
              </div>
              <div className="forum-archive-row" style={{ opacity: 0.7 }}>
                <span className="forum-archive-icon">🖼️</span>
                <span className="forum-archive-name">معارض سابقة</span>
                <span className="forum-archive-count">أرشيف المعارض</span>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

export default function Forum() {
  return (
    <ForumProvider>
      <ForumHome />
    </ForumProvider>
  );
}
